import { useCallback, useEffect, useState } from "react";
import { NGROK } from "../../../APIs";
import { myLocalStorage } from "../../StorageHelper";
import useUserStore from "../../../services/userStore";
import { apiClient } from "../../../utils/apiClient";
import { transformAlertsResponse } from "../Alerts/helper";
import type { NotificationSeverity } from "./Api/adminNotificationApi";

const POLL_INTERVAL_MS = 30000;
const ALERT_LIMIT = 100;

export interface EndpointSeverityAlert {
  id: string;
  severity: Extract<NotificationSeverity, "CRITICAL" | "HIGH">;
  title: string;
  details: string;
  timeLabel: string;
}

function currentTenantName(): string {
  const latestTenant = myLocalStorage.getItem("latestTenant");
  return latestTenant?.tenantName || (typeof latestTenant === "string" ? latestTenant : "") || "";
}

function toSeverity(level: string): EndpointSeverityAlert["severity"] | null {
  switch (level.trim().toLowerCase()) {
    case "critical":
      return "CRITICAL";
    case "high":
      return "HIGH";
    default:
      return null;
  }
}

export default function useEndpointSeverityAlerts(open: boolean) {
  const email = useUserStore((state: { user?: { email?: string } }) => state.user?.email || "");
  const [alerts, setAlerts] = useState<EndpointSeverityAlert[]>([]);

  const load = useCallback(async (signal?: AbortSignal) => {
    const tenantName = currentTenantName();
    if (!email || !tenantName) return;
    try {
      const response = await apiClient<unknown>({
        method: "GET",
        url: `${NGROK}/api/influxdb/influxdblogsdata`,
        params: {
          bucketName: "ondevice-auditlog-alerts",
          timingMode: "lw",
          email,
          tenantName,
          limit: ALERT_LIMIT,
          limitNeeded: "true",
        },
        signal,
      });
      if (signal?.aborted) return;
      const rows = Array.isArray(response) ? response : [];
      const sorted = [...rows].sort(
        (a, b) => new Date(b?._time || 0).getTime() - new Date(a?._time || 0).getTime(),
      );
      const next = transformAlertsResponse(sorted).flatMap((alert, index) => {
        const severity = toSeverity(alert.level || "");
        if (!severity) return [];
        const details = [alert.computerName, alert.user, alert.processName].filter(Boolean).join(" · ");
        return [{
          id: `${alert.id}-${index}`,
          severity,
          title: alert.message || "Security alert",
          details,
          timeLabel: alert.updatedAt || "",
        }];
      });
      setAlerts(next);
    } catch {
      return;
    }
  }, [email]);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    const timer = window.setInterval(() => {
      if (!document.hidden) load();
    }, POLL_INTERVAL_MS);
    return () => {
      controller.abort();
      window.clearInterval(timer);
    };
  }, [load]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  return alerts;
}
